import type { ReadonlyURLSearchParams } from "next/navigation";

import uniq from "lodash/uniq";

import { getSupportedLocalesArray } from "@/config/locale.config";
import { SORT_KEY } from "@/ui/SearchResultHeader";

/**
 * Removes language prefix from URL if present
 * This is necessary for correct work with next-intl Link component,
 * which automatically adds language prefix
 *
 * @param url URL, который может содержать языковой префикс
 * @returns URL без языкового префикса
 *
 * @example
 * removeLocalePrefix('/en/primary/apartment/athens/') → '/primary/apartment/athens/'
 * removeLocalePrefix('/ru/rent/villa/moscow/') → '/rent/villa/moscow/'
 * removeLocalePrefix('/some/path/without/locale/') → '/some/path/without/locale/'
 */
export function removeLocalePrefix(url: string): string {
  if (!url || typeof url !== "string") {
    return url;
  }

  // Ensure URL starts with /
  const normalizedUrl = url.startsWith("/") ? url : `/${url}`;

  // Get list of supported locales
  const supportedLocales = getSupportedLocalesArray();

  // Check if URL starts with language prefix
  for (const locale of supportedLocales) {
    const localePrefix = `/${locale}/`;
    if (normalizedUrl.startsWith(localePrefix)) {
      // Remove language prefix, keeping the rest of the path
      return normalizedUrl.substring(locale.length + 1); // +1 to remove the first '/'
    }
  }

  // If language prefix not found, return original URL
  return normalizedUrl;
}

/**
 * Проверяет, содержит ли URL языковой префикс
 *
 * @param url URL для проверки
 * @returns true, если URL содержит языковой префикс
 */
export function hasLocalePrefix(url: string): boolean {
  if (!url || typeof url !== "string") {
    return false;
  }

  const normalizedUrl = url.startsWith("/") ? url : `/${url}`;
  const supportedLocales = getSupportedLocalesArray();

  return supportedLocales.some((locale) =>
    normalizedUrl.startsWith(`/${locale}/`),
  );
}

/**
 * Определяет языковой префикс из URL, если он присутствует
 *
 * @param url URL для анализа
 * @returns языковой код или null, если префикс не найден
 */
export function extractLocaleFromUrl(url: string): string | null {
  if (!url || typeof url !== "string") {
    return null;
  }

  const normalizedUrl = url.startsWith("/") ? url : `/${url}`;
  const supportedLocales = getSupportedLocalesArray();

  for (const locale of supportedLocales) {
    if (normalizedUrl.startsWith(`/${locale}/`)) {
      return locale;
    }
  }

  return null;
}

// export const objectFromSearchParams = (
//   searchParams: ReadonlyURLSearchParams,
// ) => {
//   return searchParams
//     .entries()
//     .reduce((acc: Record<string, string | string[]>, [key, value]) => {
//       // Normalize key: remove trailing [] if present
//       const normalizedKey = key.replace(/\[\]$/, "");
//
//       if (acc[normalizedKey]) {
//         acc[normalizedKey] = Array.isArray(acc[normalizedKey])
//           ? [...acc[normalizedKey], value]
//           : [acc[normalizedKey], value];
//       } else {
//         acc[normalizedKey] = value;
//       }
//       return acc;
//     }, {});
// };

export const objectFromSearchParams = (
  searchParams: ReadonlyURLSearchParams,
) => {
  return Array.from(searchParams.entries()).reduce(
    (acc: Record<string, string | string[]>, [key, value]) => {
      const normalizedKey = key.replace(/\[\]$/, "");

      if (acc[normalizedKey]) {
        acc[normalizedKey] = Array.isArray(acc[normalizedKey])
          ? [...acc[normalizedKey], value]
          : [acc[normalizedKey], value];
      } else {
        acc[normalizedKey] = value;
      }

      return acc;
    },
    {},
  );
};

export const getRelatedLinkHref = (
  url: string | null,
  searchParams: URLSearchParams,
) => {
  const baseUrl = removeLocalePrefix(url || "#");
  if (!baseUrl || baseUrl === "#") return "#";

  const limit = searchParams.get("limit");
  const sort = searchParams.get("sort") ?? localStorage.getItem(SORT_KEY);

  const query = new URLSearchParams();
  if (limit) query.set("limit", limit);
  if (sort) query.set("sort", sort);

  return query.toString() ? `${baseUrl}?${query.toString()}` : baseUrl;
};

export const mergeUnique = (
  existing: string | string[] | undefined,
  value: string,
) => {
  const arr = existing ? (Array.isArray(existing) ? existing : [existing]) : [];

  return uniq([...arr, value]);
};

export function normalizeUrlSpaces(value: string, separator: string = "-") {
  return decodeURIComponent(value)
    .trim()
    .replace(/\s+/g, separator)
    .replace(new RegExp(`${separator}+`, "g"), separator);
}

export function sanitizeAndNormalizeSlug(
  slug: string[],
  {
    separator = "-",
    ignoreSlugPart = "_landing",
  }: {
    separator?: string;
    ignoreSlugPart?: string;
  } = {},
) {
  // Remove internal "_landing" prefix if present
  const cleanedSlug = slug[0] === ignoreSlugPart ? slug.slice(1) : slug;

  let hasInvalidChars = false;

  const normalizedSlug = cleanedSlug.map((part) => {
    const decoded = decodeURIComponent(part);

    const hasSpaces = /\s+/.test(decoded);

    if (hasSpaces) {
      hasInvalidChars = true;
      return normalizeUrlSpaces(decoded, separator);
    }

    return decoded;
  });

  return {
    cleanedSlug,
    normalizedSlug,
    hasInvalidChars,
  };
}
